// src/components/ProfessionalRatings.js
/**
 * Professional Ratings Component
 * Displays aggregated professional ratings from multiple sources with credibility weighting
 */

import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import "./ProfessionalRatings.css";

const ProfessionalRatings = ({
  beverage,
  showDetails = true,
  compact = false,
}) => {
  const [ratingsData, setRatingsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRating, setSelectedRating] = useState(null);

  useEffect(() => {
    if (beverage?.aiEnhanced?.ratings) {
      processRatingsData(beverage.aiEnhanced.ratings);
    } else {
      setLoading(false);
    }
  }, [beverage]);

  const processRatingsData = useCallback((ratings) => {
    try {
      // Calculate weighted average based on source credibility
      const sourceCredibility = {
        "Wine Spectator": 0.95,
        "Robert Parker": 0.95,
        "James Suckling": 0.9,
        "Jancis Robinson": 0.9,
        "Wine Enthusiast": 0.85,
        Decanter: 0.85,
        Untappd: 0.8,
        RateBeer: 0.75,
        BeerAdvocate: 0.75,
        "Whisky Magazine": 0.85,
        "Jim Murray": 0.8,
        default: 0.7,
      };

      const processedRatings = ratings.map((rating) => {
        const credibility =
          sourceCredibility[rating.source] || sourceCredibility.default;

        // Normalize scores to 100-point scale
        let normalizedScore;
        switch (rating.scale) {
          case 20:
            normalizedScore = (rating.score / 20) * 100;
            break;
          case 5:
            normalizedScore = (rating.score / 5) * 100;
            break;
          case 10:
            normalizedScore = (rating.score / 10) * 100;
            break;
          default:
            normalizedScore = rating.score;
        }

        return {
          ...rating,
          normalizedScore,
          credibility,
          weightedScore: normalizedScore * credibility,
        };
      });

      // Calculate aggregate metrics
      const totalWeight = processedRatings.reduce(
        (sum, rating) => sum + rating.credibility,
        0
      );
      const weightedAverage =
        processedRatings.reduce(
          (sum, rating) => sum + rating.weightedScore,
          0
        ) / totalWeight;

      const ratingCounts = processedRatings.reduce((counts, rating) => {
        const tier = getRatingTier(rating.normalizedScore);
        counts[tier] = (counts[tier] || 0) + 1;
        return counts;
      }, {});

      setRatingsData({
        ratings: processedRatings,
        aggregate: {
          weightedAverage: Math.round(weightedAverage),
          totalRatings: processedRatings.length,
          averageScore:
            processedRatings.reduce((sum, r) => sum + r.normalizedScore, 0) /
            processedRatings.length,
          ratingCounts,
          consensus: calculateConsensus(processedRatings),
        },
      });

      setLoading(false);
    } catch (error) {
      console.error("Error processing ratings data:", error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (beverage?.aiEnhanced?.ratings) {
      processRatingsData(beverage.aiEnhanced.ratings);
    } else {
      setLoading(false);
    }
  }, [beverage, processRatingsData]);

  const getRatingTier = (score) => {
    if (score >= 95) return "Exceptional";
    if (score >= 90) return "Outstanding";
    if (score >= 85) return "Very Good";
    if (score >= 80) return "Good";
    return "Fair";
  };

  const calculateConsensus = (ratings) => {
    const scores = ratings.map((r) => r.normalizedScore);
    const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    const variance =
      scores.reduce((sum, score) => sum + Math.pow(score - mean, 2), 0) /
      scores.length;
    const standardDeviation = Math.sqrt(variance);

    if (standardDeviation <= 3) return "High Consensus";
    if (standardDeviation <= 6) return "Moderate Consensus";
    return "Mixed Reviews";
  };

  const formatScore = (score, scale) => {
    switch (scale) {
      case 20:
        return `${score}/20`;
      case 5:
        return `${score}/5`;
      case 10:
        return `${score}/10`;
      default:
        return `${score}/100`;
    }
  };

  const getScoreColor = (normalizedScore) => {
    if (normalizedScore >= 95) return "#1e7e34"; // Exceptional - Dark Green
    if (normalizedScore >= 90) return "#28a745"; // Outstanding - Green
    if (normalizedScore >= 85) return "#ffc107"; // Very Good - Yellow
    if (normalizedScore >= 80) return "#fd7e14"; // Good - Orange
    return "#dc3545"; // Fair - Red
  };

  const getTierScore = (tier) => {
    switch (tier) {
      case "Exceptional":
        return 97;
      case "Outstanding":
        return 92;
      case "Very Good":
        return 87;
      case "Good":
        return 82;
      default:
        return 75;
    }
  };

  const getSourceLogo = (source) => {
    // In a real implementation, these would be actual logo URLs
    const logos = {
      "Wine Spectator": "🍷",
      "Robert Parker": "🎖️",
      "James Suckling": "🍇",
      "Wine Enthusiast": "🍾",
      Untappd: "🍺",
      RateBeer: "🍻",
      BeerAdvocate: "🍺",
      "Whisky Magazine": "🥃",
      default: "⭐",
    };
    return logos[source] || logos.default;
  };

  if (loading) {
    return (
      <div className={`professional-ratings ${compact ? "compact" : ""}`}>
        <div className="ratings-loading">
          <div className="loading-spinner"></div>
          <span>Loading professional ratings...</span>
        </div>
      </div>
    );
  }

  if (!ratingsData || ratingsData.ratings.length === 0) {
    return (
      <div className={`professional-ratings ${compact ? "compact" : ""}`}>
        <div className="no-ratings">
          <span className="no-ratings-icon">📊</span>
          <p>Professional ratings not yet available</p>
          {!compact && (
            <small>
              Ratings will be added as our AI gathers more professional reviews
            </small>
          )}
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="professional-ratings compact">
        <div className="ratings-summary">
          <div className="aggregate-score">
            <span
              className="score-value"
              style={{
                color: getScoreColor(ratingsData.aggregate.weightedAverage),
              }}
            >
              {ratingsData.aggregate.weightedAverage}
            </span>
            <span className="score-scale">/100</span>
          </div>
          <div className="ratings-meta">
            <span className="ratings-count">
              {ratingsData.aggregate.totalRatings} reviews
            </span>
            <span className="consensus-badge">
              {ratingsData.aggregate.consensus}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="professional-ratings">
      <div className="ratings-header">
        <h3>Professional Ratings</h3>
        <div className="ratings-summary-badge">
          <span className="aggregate-score-large">
            {ratingsData.aggregate.weightedAverage}
          </span>
          <div className="ratings-meta-large">
            <span>
              {ratingsData.aggregate.totalRatings} Professional Reviews
            </span>
            <span className="consensus">{ratingsData.aggregate.consensus}</span>
          </div>
        </div>
      </div>

      {showDetails && (
        <>
          <div className="ratings-distribution">
            <h4>Rating Distribution</h4>
            <div className="distribution-chart">
              {Object.entries(ratingsData.aggregate.ratingCounts).map(
                ([tier, count]) => (
                  <div key={tier} className="distribution-bar">
                    <span className="tier-label">{tier}</span>
                    <div className="bar-container">
                      <div
                        className="bar-fill"
                        style={{
                          width: `${
                            (count / ratingsData.aggregate.totalRatings) * 100
                          }%`,
                          backgroundColor: getScoreColor(getTierScore(tier)),
                        }}
                      />
                    </div>
                    <span className="count-label">{count}</span>
                  </div>
                )
              )}
            </div>
          </div>

          <div className="individual-ratings">
            <h4>Individual Ratings</h4>
            <div className="ratings-grid">
              {ratingsData.ratings.map((rating, index) => (
                <button
                  key={`rating-${rating.source}-${index}`}
                  className={`rating-card ${
                    selectedRating === index ? "selected" : ""
                  }`}
                  onClick={() =>
                    setSelectedRating(selectedRating === index ? null : index)
                  }
                  type="button"
                  aria-expanded={selectedRating === index}
                  aria-label={`View details for ${rating.source} rating`}
                >
                  <div className="rating-header">
                    <div className="source-info">
                      <span className="source-logo">
                        {getSourceLogo(rating.source)}
                      </span>
                      <span className="source-name">{rating.source}</span>
                    </div>
                    <div className="credibility-badge">
                      <span>
                        Credibility: {Math.round(rating.credibility * 100)}%
                      </span>
                    </div>
                  </div>

                  <div className="score-display">
                    <span
                      className="score-main"
                      style={{ color: getScoreColor(rating.normalizedScore) }}
                    >
                      {formatScore(rating.score, rating.scale)}
                    </span>
                    <span className="score-normalized">
                      ({Math.round(rating.normalizedScore)}/100)
                    </span>
                  </div>

                  {rating.reviewerName && (
                    <div className="reviewer-info">
                      <span>by {rating.reviewerName}</span>
                    </div>
                  )}

                  {rating.reviewDate && (
                    <div className="review-date">
                      {new Date(rating.reviewDate).toLocaleDateString()}
                    </div>
                  )}

                  {selectedRating === index && rating.review && (
                    <div className="rating-review">
                      <p>"{rating.review}"</p>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="ratings-methodology">
            <details>
              <summary>How we calculate ratings</summary>
              <div className="methodology-content">
                <p>Our aggregate score uses credibility-weighted averaging:</p>
                <ul>
                  <li>
                    <strong>Source Credibility:</strong> Each rating source has
                    a credibility weight based on industry reputation
                  </li>
                  <li>
                    <strong>Score Normalization:</strong> All scores are
                    converted to a 100-point scale for comparison
                  </li>
                  <li>
                    <strong>Weighted Average:</strong> Scores are weighted by
                    source credibility before averaging
                  </li>
                  <li>
                    <strong>Consensus Calculation:</strong> Based on standard
                    deviation of normalized scores
                  </li>
                </ul>
              </div>
            </details>
          </div>
        </>
      )}
    </div>
  );
};

ProfessionalRatings.propTypes = {
  beverage: PropTypes.shape({
    aiEnhanced: PropTypes.shape({
      ratings: PropTypes.arrayOf(
        PropTypes.shape({
          source: PropTypes.string.isRequired,
          score: PropTypes.number.isRequired,
          scale: PropTypes.number.isRequired,
          review: PropTypes.string,
          reviewDate: PropTypes.string,
          reviewerName: PropTypes.string,
        })
      ),
    }),
  }),
  showDetails: PropTypes.bool,
  compact: PropTypes.bool,
};

ProfessionalRatings.defaultProps = {
  beverage: null,
  showDetails: true,
  compact: false,
};

export default ProfessionalRatings;
